import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react"
import Refresh from '@mui/icons-material/Refresh'
import PlayArrow from '@mui/icons-material/PlayArrow'
import { PREVIEW_EVENTS } from '../constants/telemetryEvents'

// Messages the parent will act on from the (untrusted, student-authored) iframe content.
const PREVIEW_MESSAGE_EVENTS = {
  PREVIEW_ERROR: true,
  PREVIEW_CLICK: true,
  PREVIEW_INPUT: true,
  PREVIEW_SCROLL: true,
  PREVIEW_SUBMIT: true,
  PREVIEW_FOCUS: true,
  PREVIEW_CONSOLE: true,
  PREVIEW_SNAPSHOT: true,
}
const MAX_PREVIEW_MESSAGE_LENGTH = 2000
// PREVIEW_SNAPSHOT carries a compressed screenshot + truncated DOM dump, so it gets its own, more generous cap.
const MAX_SNAPSHOT_MESSAGE_LENGTH = 300000

const VIEWPORT_PRESETS = [
  { label: 'Desktop', width: null },
  { label: 'Tablet', width: 768 },
  { label: 'Mobile', width: 375 },
]

const LivePreview = forwardRef(function LivePreview({
  html = "",
  css = "",
  js = "",
  onRender,
  onTelemetry,
  renderLabel = 'Render',
  renderMeta,
  hasRendered = true,
  disableInteraction = false,
  visualTraceEnabled = false,
  mobilePreviewEnabled = false,
  title = 'Web Preview',
  badge = 'Interactive',
  showRefresh = true,
  // Browser-chrome-style header (decorative back/forward + a URL-bar-style label) instead
  // of the plain title/badge bar - added for InitialFinalJS's CodePen-style layout. Purely
  // a different header rendering; every existing control (Render, Refresh, viewport
  // presets) still renders and still works the same way, just inside the new chrome.
  chromeStyle = false,
}, ref) {
  const [srcDoc, setSrcDoc] = useState("")
  const [viewportPreset, setViewportPreset] = useState(VIEWPORT_PRESETS[0])
  const iframeRef = useRef(null)

  useEffect(() => {
    if (!hasRendered) {
      setSrcDoc("")
      return undefined
    }

    const timeout = setTimeout(() => {
      // Combined document with instrumentation (interaction + console) injected below
      const doc = `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src https: data:; form-action 'none';">
          <title>Live Preview</title>
          <style>
            /* Base reset for preview iframe */
            html, body {
              margin: 0;
              padding: 16px;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
              background-color: #ffffff;
              color: #171717;
            }
            ${css}
          </style>
        </head>
        <body>
          ${html}
          <script>
            // Interaction instrumentation, registered in its own script block so a
            // syntax error in the student's JS below can't prevent these listeners
            // (or the error handler) from being attached.
            (function () {
              function describeTarget(el) {
                if (!el || el.nodeType !== 1) return null;
                var classes = typeof el.className === 'string'
                  ? el.className.split(' ').filter(Boolean).slice(0, 3)
                  : null;
                // Only report text for leaf-ish elements (no element children) - textContent on
                // an ancestor concatenates its whole subtree, which would leak far more of the
                // page than the click/focus/submit actually targeted.
                var text = (el.textContent && el.children.length === 0)
                  ? el.textContent.trim().slice(0, 40)
                  : null;
                var rect = null, visible = null, overflow = null;
                try {
                  var r = el.getBoundingClientRect();
                  rect = { x: Math.round(r.x), y: Math.round(r.y), width: Math.round(r.width), height: Math.round(r.height) };
                  var style = window.getComputedStyle(el);
                  visible = style.display !== 'none' && style.visibility !== 'hidden' && r.width > 0 && r.height > 0;
                  overflow = style.overflow;
                } catch (e) {}
                return {
                  tag: el.tagName ? el.tagName.toLowerCase() : null,
                  id: el.id || null,
                  classes: classes,
                  text: text,
                  rect: rect,
                  visible: visible,
                  overflow: overflow,
                };
              }
              document.addEventListener('click', function (e) {
                window.parent.postMessage({ type: 'PREVIEW_CLICK', target: describeTarget(e.target) }, '*');
              }, true);
              document.addEventListener('input', function (e) {
                var el = e.target;
                var detail = describeTarget(el);
                if (el && (el.type === 'checkbox' || el.type === 'radio')) {
                  detail.checked = el.checked;
                } else if (el && 'value' in el) {
                  // Log length only, never the raw value, in case the student's demo form holds sensitive-looking input.
                  detail.valueLength = String(el.value).length;
                }
                window.parent.postMessage({ type: 'PREVIEW_INPUT', target: detail }, '*');
              }, true);
              document.addEventListener('submit', function (e) {
                window.parent.postMessage({ type: 'PREVIEW_SUBMIT', target: describeTarget(e.target) }, '*');
              }, true);
              document.addEventListener('focusin', function (e) {
                window.parent.postMessage({ type: 'PREVIEW_FOCUS', focused: true, target: describeTarget(e.target) }, '*');
              }, true);
              document.addEventListener('focusout', function (e) {
                window.parent.postMessage({ type: 'PREVIEW_FOCUS', focused: false, target: describeTarget(e.target) }, '*');
              }, true);
              var scrollTimer = null;
              window.addEventListener('scroll', function () {
                clearTimeout(scrollTimer);
                scrollTimer = setTimeout(function () {
                  window.parent.postMessage({ type: 'PREVIEW_SCROLL', scrollY: window.scrollY }, '*');
                }, 200);
              }, true);

              // Relay console output so it's visible in telemetry, while still calling
              // through to the real console methods so on-page debugging still works.
              var nativeConsole = { log: console.log, warn: console.warn, error: console.error, info: console.info };
              // Rate limit: the preview now stays mounted across tab switches (it no longer
              // unmounts when the student views the AI tab), so a stray setInterval loop in
              // student code that logs would otherwise relay unbounded events forever.
              var consoleRateWindowStart = Date.now();
              var consoleRateCount = 0;
              var CONSOLE_RATE_MAX = 20;
              var CONSOLE_RATE_WINDOW_MS = 5000;
              function relayConsole(level, args) {
                var now = Date.now();
                if (now - consoleRateWindowStart > CONSOLE_RATE_WINDOW_MS) {
                  consoleRateWindowStart = now;
                  consoleRateCount = 0;
                }
                consoleRateCount += 1;
                if (consoleRateCount > CONSOLE_RATE_MAX) {
                  if (consoleRateCount === CONSOLE_RATE_MAX + 1) {
                    window.parent.postMessage({ type: 'PREVIEW_CONSOLE', level: 'warn', message: '[console output rate-limited - too many messages in a short window]' }, '*');
                  }
                  return;
                }
                var text;
                try {
                  text = Array.prototype.map.call(args, function (a) {
                    if (typeof a === 'string') return a;
                    try { return JSON.stringify(a); } catch (e) { return String(a); }
                  }).join(' ').slice(0, 500);
                } catch (e) {
                  text = '[unserializable console output]';
                }
                window.parent.postMessage({ type: 'PREVIEW_CONSOLE', level: level, message: text }, '*');
              }
              ['log', 'warn', 'error', 'info'].forEach(function (level) {
                console[level] = function () {
                  relayConsole(level, arguments);
                  nativeConsole[level].apply(console, arguments);
                };
              });

              // Dependency-free screenshot: DOM -> SVG foreignObject -> canvas -> JPEG dataURL. Avoids an external screenshot lib and allow-same-origin; not pixel-perfect.
              // Always defined (not gated on visualTraceEnabled) so the student's own "attach screenshot"
              // button in the AI chat works even in sessions where background auto-capture is off - that's
              // a deliberate, visible action the student takes, not passive collection.
              function captureSnapshot(requestId) {
                try {
                  var w = Math.max(document.documentElement.scrollWidth, window.innerWidth);
                  var h = Math.max(document.documentElement.scrollHeight, window.innerHeight);
                  var htmlSource = new XMLSerializer().serializeToString(document.documentElement);
                  var svg = '<svg xmlns="http://www.w3.org/2000/svg" width="' + w + '" height="' + h + '">' +
                    '<foreignObject width="100%" height="100%">' + htmlSource + '</foreignObject></svg>';
                  var img = new Image();
                  img.onload = function () {
                    var screenshot = null;
                    try {
                      var canvas = document.createElement('canvas');
                      var scale = 0.5;
                      canvas.width = Math.max(1, Math.round(w * scale));
                      canvas.height = Math.max(1, Math.round(h * scale));
                      var ctx = canvas.getContext('2d');
                      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                      screenshot = canvas.toDataURL('image/jpeg', 0.5);
                    } catch (e) {
                      // Likely a canvas-tainting cross-origin image in the student's HTML - skip the screenshot, still send the DOM snapshot.
                    }
                    window.parent.postMessage({
                      type: 'PREVIEW_SNAPSHOT',
                      requestId: requestId || null,
                      screenshot: screenshot,
                      domSnapshot: document.documentElement.outerHTML.slice(0, 8000),
                      viewport: { width: window.innerWidth, height: window.innerHeight }
                    }, '*');
                  };
                  img.onerror = function () {
                    window.parent.postMessage({
                      type: 'PREVIEW_SNAPSHOT',
                      requestId: requestId || null,
                      screenshot: null,
                      domSnapshot: document.documentElement.outerHTML.slice(0, 8000),
                      viewport: { width: window.innerWidth, height: window.innerHeight }
                    }, '*');
                  };
                  img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
                } catch (e) {}
              }
              ${visualTraceEnabled ? `
              window.addEventListener('load', function () {
                setTimeout(captureSnapshot, 0);
              });
              ` : ''}
              window.addEventListener('message', function (e) {
                if (e.data && e.data.type === 'REQUEST_SNAPSHOT') {
                  captureSnapshot(e.data.requestId);
                }
              });
            })();
          </script>
          <script>
            // Simple interceptor to handle errors gracefully
            window.addEventListener('error', function(e) {
              window.parent.postMessage({ type: 'PREVIEW_ERROR', message: e.message }, '*');
            });
            try {
              ${js}
            } catch (err) {
              window.parent.postMessage({ type: 'PREVIEW_ERROR', message: err.message }, '*');
            }
          </script>
        </body>
        </html>
      `
      setSrcDoc(doc)
    }, 400) // 400ms debounce to make sure typing feels smooth

    return () => clearTimeout(timeout)
  }, [html, css, js, hasRendered, visualTraceEnabled])

  // Read via refs, not effect deps - renderMeta is a fresh object literal every render, which would otherwise tear down/re-add this listener on every keystroke.
  const onTelemetryRef = useRef(onTelemetry)
  onTelemetryRef.current = onTelemetry
  const renderMetaRef = useRef(renderMeta)
  renderMetaRef.current = renderMeta
  const visualTraceRef = useRef(visualTraceEnabled)
  visualTraceRef.current = visualTraceEnabled

  // Pending imperative capture requests (from requestSnapshot/requestBurst), keyed by requestId,
  // so a manually-requested snapshot can be routed back to its caller instead of only being logged.
  const pendingCapturesRef = useRef(new Map())

  useEffect(() => {
    const handlePreviewMessage = (event) => {
      if (event.source !== iframeRef.current?.contentWindow) return

      // The iframe runs student-authored code, so treat its messages as untrusted:
      // only react to known event types and cap the size of anything we log.
      const data = event.data
      const type = data?.type
      if (!PREVIEW_MESSAGE_EVENTS[type]) return
      const cap = type === 'PREVIEW_SNAPSHOT' ? MAX_SNAPSHOT_MESSAGE_LENGTH : MAX_PREVIEW_MESSAGE_LENGTH
      if (JSON.stringify(data).length > cap) return
      // A snapshot with a requestId came from requestSnapshot/requestBurst below (a deliberate
      // student click), so it's allowed through regardless of visualTraceEnabled - only the
      // unsolicited, load-triggered auto-capture is gated by that flag.
      const isManualCapture = type === 'PREVIEW_SNAPSHOT' && pendingCapturesRef.current.has(data.requestId)
      if (type === 'PREVIEW_SNAPSHOT' && !visualTraceRef.current && !isManualCapture) return

      if (isManualCapture) {
        const resolve = pendingCapturesRef.current.get(data.requestId)
        pendingCapturesRef.current.delete(data.requestId)
        resolve({ screenshot: data.screenshot, domSnapshot: data.domSnapshot, viewport: data.viewport })
      }

      const meta = { ...renderMetaRef.current, timestamp: Date.now() }

      if (type === 'PREVIEW_ERROR') {
        onTelemetryRef.current?.(PREVIEW_EVENTS.ERROR, { message: data.message, ...meta })
      } else if (type === 'PREVIEW_CLICK') {
        onTelemetryRef.current?.(PREVIEW_EVENTS.CLICK, { target: data.target, ...meta })
      } else if (type === 'PREVIEW_INPUT') {
        onTelemetryRef.current?.(PREVIEW_EVENTS.INPUT, { target: data.target, ...meta })
      } else if (type === 'PREVIEW_SCROLL') {
        onTelemetryRef.current?.(PREVIEW_EVENTS.SCROLL, { scrollY: data.scrollY, ...meta })
      } else if (type === 'PREVIEW_SUBMIT') {
        onTelemetryRef.current?.(PREVIEW_EVENTS.SUBMIT, { target: data.target, ...meta })
      } else if (type === 'PREVIEW_FOCUS') {
        onTelemetryRef.current?.(PREVIEW_EVENTS.FOCUS, { focused: data.focused, target: data.target, ...meta })
      } else if (type === 'PREVIEW_CONSOLE') {
        onTelemetryRef.current?.(PREVIEW_EVENTS.CONSOLE, { level: data.level, message: data.message, ...meta })
      } else if (type === 'PREVIEW_SNAPSHOT') {
        onTelemetryRef.current?.(PREVIEW_EVENTS.SNAPSHOT, {
          trigger: isManualCapture ? 'manual' : 'auto',
          screenshot: data.screenshot,
          domSnapshot: data.domSnapshot,
          viewport: data.viewport,
          ...meta,
        })
      }
    }

    window.addEventListener('message', handlePreviewMessage)
    return () => window.removeEventListener('message', handlePreviewMessage)
  }, [])

  // Requests one capture from the live iframe and resolves with its payload (or null on
  // timeout/no iframe). Used by the AI chat's screenshot/record buttons - see useImperativeHandle below.
  const requestSnapshot = () => {
    const contentWindow = iframeRef.current?.contentWindow
    if (!contentWindow) return Promise.resolve(null)
    const requestId = `req_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        pendingCapturesRef.current.delete(requestId)
        resolve(null)
      }, 4000)
      pendingCapturesRef.current.set(requestId, (result) => {
        clearTimeout(timeout)
        resolve(result)
      })
      contentWindow.postMessage({ type: 'REQUEST_SNAPSHOT', requestId }, '*')
    })
  }

  // Samples requestSnapshot() repeatedly for the "record a short clip" button - up to
  // maxFrames, roughly `intervalMs` apart, stopping early if the iframe stops responding.
  const requestBurst = async (durationMs = 10000, intervalMs = 1000, onFrame) => {
    const frames = []
    const maxFrames = Math.max(1, Math.round(durationMs / intervalMs))
    for (let i = 0; i < maxFrames; i += 1) {
      const frame = await requestSnapshot()
      if (!frame) break
      frames.push(frame)
      onFrame?.(frames.length, maxFrames)
      if (i < maxFrames - 1) await new Promise((r) => setTimeout(r, intervalMs))
    }
    return frames
  }

  // Lets a caller (Workspace.jsx's whole-workspace capture) composite this iframe's own
  // requestSnapshot() image onto a wider outer-DOM screenshot at the right position - the
  // iframe's rendered content isn't otherwise visible to an outer-page screenshot technique.
  const getBoundingRect = () => iframeRef.current?.getBoundingClientRect() || null

  useImperativeHandle(ref, () => ({ requestSnapshot, requestBurst, getBoundingRect }))

  const forceRefresh = () => {
    onTelemetry?.(PREVIEW_EVENTS.RELOAD, {
      ...renderMeta,
      timestamp: Date.now(),
    })
    const doc = srcDoc
    setSrcDoc("")
    setTimeout(() => {
      setSrcDoc(doc)
    }, 50)
  }

  const handleViewportChange = (preset) => {
    setViewportPreset(preset)
    onTelemetry?.(PREVIEW_EVENTS.VIEWPORT_CHANGE, {
      width: preset.width,
      label: preset.label,
      ...renderMeta,
      timestamp: Date.now(),
    })
  }

  return (
    <div className="ws-chat-container" style={{ display: 'flex', flexDirection: 'column', height: '100%', borderLeft: '1px solid #e5e5e5' }}>
      {/* Header bar matching the AI Assistant header layout style, or (chromeStyle) a
          browser-chrome look for InitialFinalJS's CodePen-style layout - same controls
          either way, just different chrome around them. */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: chromeStyle ? '8px 12px' : '10px 16px',
        gap: '10px',
        borderBottom: '1px solid #e5e5e5',
        backgroundColor: chromeStyle ? '#f3f4f6' : '#ffffff'
      }}>
        {chromeStyle ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
            {/* Decorative only - a real preview pane has nowhere to navigate back/forward
                to, this is purely the visual "browser chrome" look. */}
            <span aria-hidden="true" style={{ color: '#c7cbd1', fontSize: '1rem', userSelect: 'none' }}>‹</span>
            <span aria-hidden="true" style={{ color: '#c7cbd1', fontSize: '1rem', userSelect: 'none' }}>›</span>
            <div style={{
              flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '6px',
              backgroundColor: '#ffffff', border: '1px solid #e5e5e5', borderRadius: '6px',
              padding: '4px 10px', overflow: 'hidden',
            }}>
              <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#171717', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {title}
              </span>
              {badge && (
                <span style={{ fontSize: '0.7rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                  {badge}
                </span>
              )}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              fontFamily: "'Open Sans', sans-serif",
              fontWeight: 600,
              fontSize: '0.9rem',
              color: '#171717'
            }}>
              {title}
            </span>
            {badge && (
              <span style={{
                fontSize: '0.75rem',
                padding: '2px 6px',
                borderRadius: '4px',
                backgroundColor: '#f3f4f6',
                color: '#4b5563'
              }}>
                {badge}
              </span>
            )}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          {mobilePreviewEnabled && (
            <div style={{ display: 'flex', gap: '2px', border: '1px solid #e5e5e5', borderRadius: '5px', padding: '2px' }}>
              {VIEWPORT_PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => handleViewportChange(preset)}
                  title={preset.width ? `${preset.width}px wide` : 'Full width'}
                  style={{
                    padding: '3px 8px', fontSize: '0.72rem', fontWeight: 600,
                    border: 'none', borderRadius: '3px', cursor: 'pointer',
                    backgroundColor: viewportPreset.label === preset.label ? '#2563eb' : 'transparent',
                    color: viewportPreset.label === preset.label ? '#ffffff' : '#4b5563',
                  }}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          )}
          {onRender && (
            <button
              onClick={onRender}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 10px',
                border: '1px solid #2563eb', borderRadius: '5px', background: '#2563eb',
                color: '#ffffff', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
              }}
            >
              <PlayArrow style={{ fontSize: '1rem' }} />
              {renderLabel}
            </button>
          )}
          {showRefresh && (
            <button
              onClick={forceRefresh}
              style={{
                background: 'none', border: 'none', color: '#737373', cursor: 'pointer',
                padding: '4px', borderRadius: '4px', display: 'flex', alignItems: 'center',
                justifyContent: 'center', transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              title="Reload the last rendered preview"
            >
              <Refresh style={{ fontSize: '1.25rem' }} />
            </button>
          )}
        </div>
      </div>

      {/* Frame Container */}
      <div
        style={{
          flex: 1,
          position: 'relative',
          background: viewportPreset.width ? '#f3f4f6' : '#ffffff',
          display: 'flex',
          justifyContent: 'center',
        }}>
        {srcDoc ? (
          <iframe
            ref={iframeRef}
            srcDoc={srcDoc}
            title="VizGroup Live Web Preview"
            // allow-forms is needed so 'submit' actually fires (sandbox otherwise swallows it silently); form-action 'none' in the CSP above blocks any exfil via a form's action URL.
            sandbox="allow-scripts allow-modals allow-forms"
            onLoad={() => onTelemetry?.(PREVIEW_EVENTS.RENDERED, {
              ...renderMeta,
              timestamp: Date.now(),
            })}
            style={{
              width: viewportPreset.width ? `${viewportPreset.width}px` : '100%',
              height: '100%',
              border: viewportPreset.width ? '1px solid #d1d5db' : 'none',
              background: '#ffffff',
              // Disabled during panel drags so the iframe's own document doesn't swallow mousemove/mouseup before the parent's drag listeners see them.
              pointerEvents: disableInteraction ? 'none' : 'auto',
            }}
          />
        ) : (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#737373',
            fontSize: '0.875rem'
          }}>
            {hasRendered ? 'Loading preview...' : `Select ${renderLabel} to preview your page.`}
          </div>
        )}
      </div>
    </div>
  )
})

export default LivePreview
